import { CheckIcon, ChevronsUpDownIcon, FolderIcon, PlusIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { PROJECT_STATUS_LABELS_RU } from '@project-os/contracts';
import { useProjects } from '@/api/projects';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ProjectLogo } from '@/components/common/project-logo';

/**
 * Переключение проекта из шапки.
 *
 * После выбора проекта весь интерфейс работает в его контексте: пользователь
 * всегда должен видеть, в каком проекте находится (документ «Структура
 * интерфейса», §2.1).
 */
export function ProjectSwitcher({ currentProjectId }: { currentProjectId?: string }) {
  const navigate = useNavigate();
  const { data: projects = [] } = useProjects();

  const current = projects.find((project) => project.id === currentProjectId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 max-w-72 gap-2 px-2">
          <FolderIcon className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-medium">{current?.name ?? 'Выберите проект'}</span>
          <ChevronsUpDownIcon className="size-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80">
        <DropdownMenuLabel className="text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
          Проекты
        </DropdownMenuLabel>
        {projects.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground">Доступных проектов нет</div>
        ) : (
          projects.map((project) => (
            <DropdownMenuItem
              key={project.id}
              onSelect={() => void navigate(`/projects/${project.id}`)}
              className="gap-2"
            >
              <CheckIcon
                className={cn(
                  'size-4 shrink-0',
                  project.id === currentProjectId ? '' : 'invisible',
                )}
              />
              <ProjectLogo project={project} size="sm" />
              <span className="min-w-0 flex-1 truncate">{project.name}</span>
              <span className="shrink-0 text-2xs text-muted-foreground">
                {PROJECT_STATUS_LABELS_RU[project.status]}
              </span>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void navigate('/projects')}>
          <PlusIcon className="size-4" />
          Все проекты
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
